import { useCallback } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  useLeads,
  usePipelineStats,
  updateLead,
  PIPELINE_STAGES,
  type CrmLeadFilters,
} from '@/hooks/useCRM'
import { type CrmLead } from '@/lib/supabase'
import { ChevronDown, Mail, Building2 } from 'lucide-react'

const STAGE_LABELS: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  replied: 'Replied',
  meeting: 'Meeting',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  won: 'Won',
  lost: 'Lost',
}

type PipelineBoardProps = {
  onSelectLead: (lead: CrmLead) => void
  filters?: CrmLeadFilters
}

export function PipelineBoard({ onSelectLead, filters }: PipelineBoardProps) {
  const { leads, loading, refetch } = useLeads(filters ?? {})
  const { stats } = usePipelineStats()

  const byStage = PIPELINE_STAGES.reduce(
    (acc, stage) => {
      acc[stage] = leads.filter((l) => l.pipeline_stage === stage)
      return acc
    },
    {} as Record<string, CrmLead[]>
  )

  const handleStageChange = useCallback(
    async (leadId: string, newStage: CrmLead['pipeline_stage']) => {
      const { error } = await updateLead(leadId, { pipeline_stage: newStage })
      if (!error) refetch()
    },
    [refetch]
  )

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {PIPELINE_STAGES.map((stage) => (
        <div
          key={stage}
          className="flex min-w-[280px] max-w-[280px] flex-col rounded-lg border bg-muted/30"
        >
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="font-medium">{STAGE_LABELS[stage] ?? stage}</span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
              {stats?.find((s) => s.pipeline_stage === stage)?.count ?? byStage[stage]?.length ?? 0}
            </span>
          </div>
          <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-2" style={{ minHeight: 200 }}>
            {(byStage[stage] ?? []).map((lead) => (
              <Card
                key={lead.id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => onSelectLead(lead)}
              >
                <CardHeader className="p-3 pb-1">
                  <div className="flex items-start justify-between gap-1">
                    <p className="truncate font-medium text-foreground">
                      {lead.full_name || lead.email || `${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim() || 'No name'}
                    </p>
                    <div className="relative">
                      <select
                        className="absolute inset-0 cursor-pointer opacity-0"
                        value={stage}
                        onChange={(e) => {
                          e.stopPropagation()
                          handleStageChange(lead.id, e.target.value as CrmLead['pipeline_stage'])
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {PIPELINE_STAGES.map((s) => (
                          <option key={s} value={s}>
                            {STAGE_LABELS[s]}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    {lead.company && (
                      <>
                        <Building2 className="h-3 w-3" />
                        <span className="truncate">{lead.company}</span>
                      </>
                    )}
                    {lead.email && (
                      <>
                        <Mail className="h-3 w-3" />
                        <span className="truncate">{lead.email}</span>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
